import { cva, VariantProps } from "class-variance-authority";
import classNames from "classnames";
import { forwardRef } from "react";

const wrapperStyles = cva(
  'flex items-center focus-within:border-app-blue-500 transition-colors overflow-hidden aria-[disabled="true"]:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary:
          'bg-app-gray-600 border-app-gray-525 aria-[disabled="true"]:bg-app-gray-300 aria-[disabled="true"]:border-app-gray-300',
        secondary:
          'bg-app-gray-400 border-app-gray-300 aria-[disabled="true"]:bg-app-gray-300 aria-[disabled="true"]:border-app-gray-300',
        tertiary:
          'bg-app-gray-50 border-app-gray-50 aria-[disabled="true"]:bg-app-gray-250 aria-[disabled="true"]:border-app-gray-250',
        filter:
          'bg-app-gray-450 border-app-gray-400 aria-[disabled="true"]:bg-app-gray-300 aria-[disabled="true"]:border-app-gray-300',
        search:
          'bg-app-gray-825 border-app-gray-335 aria-[disabled="true"]:bg-app-gray-300 aria-[disabled="true"]:border-app-gray-300',
        dark: 'bg-app-gray-775 border-app-gray-335 aria-[disabled="true"]:bg-app-gray-300 aria-[disabled="true"]:border-app-gray-300',
        moreDark:
          'bg-[#061B2B] border-[#2181CA] aria-[disabled="true"]:bg-app-gray-300 aria-[disabled="true"]:border-app-gray-300',
        light:
          'bg-app-gray-625 border-app-gray-460 aria-[disabled="true"]:bg-app-gray-300 aria-[disabled="true"]:border-app-gray-300',
        sidebar:
          'bg-[#1E1E1E] border-[#505A62] aria-[disabled="true"]:bg-app-gray-300 aria-[disabled="true"]:border-app-gray-300',
      },
      size: {
        lg: "rounded-md border-2",
        md: "rounded-lg border",
      },
      hasError: {
        true: "border-app-red-500",
      },
    },
  },
);

const inputStyles = cva(
  "min-w-0 focus:outline-none bg-transparent flex-1 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary: "text-white placeholder:text-app-gray-200",
        secondary: "text-white placeholder:text-app-gray-50",
        tertiary: "text-app-gray-800 placeholder:text-app-gray-500",
        filter: "text-white placeholder:text-white/50",
        search: "text-white placeholder:text-app-gray-200",
        dark: "text-white placeholder:text-app-gray-200",
        moreDark: "text-white placeholder:text-app-gray-200",
        light: "text-white placeholder:text-app-gray-200",
        sidebar: "text-white placeholder:text-app-gray-335",
      },
      size: {
        lg: "xs:text-lg sm:text-2xl px-2 py-1.5 xs:px-2.5 xs:py-2 sm:px-4 sm:py-3",
        md: "text-xs 2xl:text-sm py-1.5 2xl:py-2 px-2 sm:px-2.5",
      },
      disabled: {
        true: "cursor-not-allowed",
      },
    },
  },
);

export type InputProps = VariantProps<typeof inputStyles> &
  VariantProps<typeof wrapperStyles> &
  Omit<
    React.ComponentPropsWithoutRef<"input">,
    keyof VariantProps<typeof inputStyles> | "name" | "size"
  > & {
    disabled?: boolean;
    wrapperClassName?: string;
    endComponent?: React.ReactNode;
    inputSize?: number;
    name: string;
    maskFn?: (value: string) => string;
  };

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      endComponent,
      variant = "primary",
      size = "md",
      wrapperClassName,
      hasError = false,
      type = "text",
      inputSize,
      disabled,
      onChange,
      maskFn,
      ...props
    },
    ref,
  ): JSX.Element => {
    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
      if (maskFn) {
        event.target.value = maskFn(event.target.value);
      }

      onChange?.(event);
    }

    return (
      <div
        aria-disabled={disabled}
        className={wrapperStyles({
          variant,
          size,
          hasError,
          class: wrapperClassName,
        })}
      >
        <input
          ref={ref}
          type={type}
          size={inputSize}
          className={inputStyles({
            variant,
            size,
            class: className,
          })}
          id={props.name}
          disabled={disabled}
          onChange={handleChange}
          {...props}
        />

        {endComponent ? (
          <div
            className={classNames("flex shrink-0 items-center justify-center", {
              "pr-2 xs:pr-2.5 sm:pr-4": size === "lg",
              "pr-2 sm:pr-2.5": size === "md",
            })}
          >
            {endComponent}
          </div>
        ) : null}
      </div>
    );
  },
);
